package com.example.mystybag.appCore.views.pages

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.example.mystybag.appCore.logic.pages.StoredCardsViewModel
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.UIView
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p2
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.sharedmodels.mystyBag.responseModels.StoredCardModel
import dev.icerock.moko.resources.compose.painterResource

@Composable
fun StoredCardsView(viewModel: StoredCardsViewModel) {
    val keyboardController = LocalSoftwareKeyboardController.current

    UIView(
        modifier = Modifier.fillMaxSize(),
        viewModel = viewModel,
        showTopBar = true,
        removeVerticalPadding = false,
        navigationContent = {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = p16),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    modifier = Modifier
                        .size(LocalTheme.p48)
                        .clip(CircleShape)
                        .background(AppTheme.current.colors.surfaceContainerLowest)
                        .noRippleClickable {
                            keyboardController?.hide()
                            viewModel.navCallbacks.backButtonCallback()
                        },
                    contentAlignment = Alignment.Center
                ) {
                    Image(
                        painter = painterResource(SRC.images.arrow_back),
                        contentDescription = null,
                        contentScale = ContentScale.Fit,
                        modifier = Modifier.size(width = 10.dp, height = 18.dp),
                        colorFilter = ColorFilter.tint(AppTheme.current.colors.primary)
                    )
                }

                Texts.TitleMedium(
                    text = sharedString(SR.strings.stored_cards_title),
                    modifier = Modifier.weight(1f),
                    textAlign = TextAlign.Center,
                    color = AppTheme.current.colors.onSurface
                )

                Spacer(modifier = Modifier.size(LocalTheme.p48))
            }
        }
    ) { uiState ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = p16, vertical = p32)
        ) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(p12)
            ) {
                Texts.LabelLarge(
                    text = sharedString(SR.strings.cards_section_title),
                    color = AppTheme.current.colors.onSurfaceVariant,
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(LocalTheme.p16)
                )
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(p2)
                        .background(AppTheme.current.colors.onSurfaceVariant.copy(alpha = 0.4f))
                )
            }

            Spacer(modifier = Modifier.height(p24))

            if (uiState.value.isLoading) {
                CircularProgressIndicator(modifier = Modifier.align(Alignment.CenterHorizontally))
            } else if (uiState.value.cards.isEmpty()) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f),
                    contentAlignment = Alignment.Center
                ) {
                    Texts.BodyMedium(
                        text = sharedString(SR.strings.no_stored_cards),
                        color = AppTheme.current.colors.onSurfaceVariant,
                        textAlign = TextAlign.Center
                    )
                }
            } else {
                LazyColumn(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(p12)
                ) {
                    items(uiState.value.cards, key = { it.id }) { card ->
                        StoredCardItem(card = card)
                    }
                }
            }
        }
    }
}

@Composable
private fun StoredCardItem(card: StoredCardModel) {
    val colors = AppTheme.current.colors
    val displayValue = card.maskedPan.ifEmpty { card.cardCustomName }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .border(
                width = LocalTheme.p1,
                color = colors.outline.copy(alpha = 0.5f),
                shape = RoundedCornerShape(LocalTheme.p20)
            )
            .padding(horizontal = p24, vertical = p16)
    ) {
        Texts.TitleMedium(
            text = displayValue,
            color = colors.onSurface
        )
    }
}
